import { format } from "date-fns";
import qrcode from "qrcode-generator";
import type { EntregaDoPedido, EtiquetaUnica } from "@bookdental/schemas";
import { formatCep } from "@/lib/viacep";
import { codigoDoPedido, codigoDoServico } from "@/lib/codigo-do-pedido";
import { esc } from "./impressao";
import type { Pagina, PedidoDaEtiqueta, ServicoDaEtiqueta } from "./etiquetas";

const FONTE = { compacta: 0.9, normal: 1, grande: 1.12 } as const;
/** Campos que ocupam a largura toda (os outros vão em duas colunas). */
// Dentes na largura toda e quebrando linha: muitos dentes não cabem em meia coluna.
const LARGOS = new Set(["servico", "dentes", "observacao", "clinica", "endereco"]);

// Tesoura em SVG (sem emoji): marca onde recortar.
const TESOURA = `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="#000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/></svg>`;

function css(cfg: EtiquetaUnica) {
  // A letra acompanha a largura: 10 cm é a referência.
  const f = FONTE[cfg.fonte] * Math.min(1.15, Math.max(0.6, cfg.largura / 100));
  const pt = (n: number) => `${(n * f).toFixed(2)}pt`;
  const qr = Math.round(Math.min(24, cfg.largura * 0.22));
  // O do catálogo cabe na expedição (com a legenda embaixo), sem passar de 16 mm.
  const qrCat = Math.max(8, Math.min(16, cfg.alturaDaExpedicao - 5));
  return `@page { size: ${cfg.largura}mm ${cfg.altura}mm; margin: 0; }
.u { width: ${cfg.largura}mm; height: ${cfg.altura}mm; padding: 2.5mm; display: flex; flex-direction: column; overflow: hidden; break-after: page; }
.u:last-child { break-after: auto; }
.parte { border: 0.35mm solid #000; border-radius: 2mm; padding: 2mm 2.6mm; overflow: hidden; display: flex; flex-direction: column; min-height: 0; }
.prod { flex: 1; }
.exp { height: ${cfg.alturaDaExpedicao}mm; flex: none; padding: 1.4mm 2.4mm; justify-content: center; position: relative; }
.corte { height: 6mm; flex: none; display: flex; align-items: center; gap: 1.5mm; font-size: ${pt(5.6)}; font-weight: 700; letter-spacing: 0.4pt; }
.corte .linha { flex: 1; border-top: 0.45mm dashed #000; }
.titulo { font-size: ${pt(6)}; font-weight: 800; letter-spacing: 0.6pt; text-transform: uppercase; }
.rot { font-size: ${pt(5.2)}; font-weight: 700; letter-spacing: 0.2pt; text-transform: uppercase; color: #333; line-height: 1.1; }
.v { font-size: ${pt(8.6)}; font-weight: 700; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cab { display: flex; gap: 2.5mm; align-items: flex-start; border-bottom: 0.3mm solid #000; padding-bottom: 1.4mm; }
.cab .min { flex: 1; min-width: 0; }
.codigo { font-size: ${pt(24)}; font-weight: 800; line-height: 1; }
.prazo { margin-top: 1mm; font-size: ${pt(10)}; font-weight: 800; }
.qr { width: ${qr}mm; height: ${qr}mm; flex: none; }
.qr svg { width: 100%; height: 100%; display: block; }
.grade { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); column-gap: 2mm; row-gap: 1mm; padding-top: 1.4mm; }
.largo { grid-column: 1 / -1; }
.prod .largo.servico .v { font-size: ${pt(12)}; white-space: normal; }
.prod .largo.observacao .v { white-space: normal; font-weight: 400; font-size: ${pt(7.6)}; }
.prod .largo.dentes .v { white-space: normal; }
.exp .grade { padding-top: 0; row-gap: 0.4mm; }
.exp .rot { font-size: ${pt(4.6)}; }
.exp .v { font-size: ${pt(7.4)}; line-height: 1.15; }
.exp .largo.clinica .v { font-size: ${pt(9.6)}; font-weight: 800; }
.exp .largo.endereco .v { white-space: normal; font-weight: 600; }
.logo { position: absolute; right: 2.4mm; top: 1.4mm; max-height: 6mm; max-width: 20mm; object-fit: contain; }
.qrcat { position: absolute; right: 2.4mm; bottom: 1.2mm; width: ${qrCat}mm; text-align: center; }
.qrcat svg { width: ${qrCat}mm; height: ${qrCat}mm; display: block; }
.qrcat span { font-size: ${pt(4.2)}; font-weight: 700; text-transform: uppercase; letter-spacing: 0.02em; }
.exp.comqr .grade { padding-right: ${qrCat + 2}mm; }
@media screen { body { background: #e4e4e7; padding: 8px; } .u { background: #fff; margin: 0 auto 8px; } }`;
}

const data = (d: Date | string | null | undefined) => (d ? format(new Date(d), "dd/MM/yyyy") : "—");
const juntar = (...partes: (string | null | undefined)[]) => partes.filter(Boolean).join(" · ");
const celula = (chave: string, rotulo: string, valorHtml: string) =>
  `<div class="${LARGOS.has(chave) ? `largo ${chave}` : chave}"><div class="rot">${esc(rotulo)}</div><div class="v">${valorHtml}</div></div>`;

/** QR em SVG com o link que abre o pedido no sistema. */
export function qrDoPedido(url: string) {
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  return qr.createSvgTag({ cellSize: 2, margin: 0, scalable: true });
}

/** O QR do catálogo com a legenda — o mesmo nas duas etiquetas que vão até o dentista. */
export const qrDoCatalogo = (url: string) => `<div class="qrcat">${qrDoPedido(url)}<span>Nosso catálogo</span></div>`;

function parteDeProducao(cfg: EtiquetaUnica, p: PedidoDaEtiqueta, s: ServicoDaEtiqueta, qr: string) {
  const on = cfg.camposProducao.filter((c) => c.ligado).map((c) => c.chave);
  const valores: Record<string, [string, string | null | undefined]> = {
    servico: ["Serviço", s.servico],
    material: ["Material", s.material],
    cor: ["Cor", s.cor],
    dentes: ["Dentes", s.dentes],
    paciente: ["Paciente", p.paciente],
    dentista: ["Dentista", p.dentista],
    clinica: ["Clínica", p.clinica],
    dataDoPedido: ["Pedido em", data(p.criadoEm)],
    etapa: ["Etapa", s.etapa],
    tecnico: ["Técnico", s.tecnico],
    observacao: ["Observação", s.observacao],
  };
  const campos = on
    .filter((c) => c !== "prazo" && (c !== "observacao" || s.observacao))
    .map((c) => celula(c, valores[c]![0], esc(valores[c]![1] || "—")))
    .join("");
  return `<div class="parte prod">
  <div class="cab">
    <div class="min">
      <div class="titulo">Produção · Pedido ${esc(codigoDoPedido(p))}</div>
      <div class="codigo">${esc(codigoDoServico(p, s))}</div>
      ${on.includes("prazo") ? `<div class="prazo">Prazo ${esc(data(p.prazo))}</div>` : ""}
    </div>
    ${cfg.qr ? `<div class="qr">${qr}</div>` : ""}
  </div>
  ${campos ? `<div class="grade">${campos}</div>` : ""}
</div>`;
}

function parteDeExpedicao(cfg: EtiquetaUnica, p: PedidoDaEtiqueta, destino: EntregaDoPedido | null, logo?: string | null, qrCatalogo = "") {
  const endereco = destino
    ? `${esc(`${destino.endereco}, ${destino.numero}${destino.complemento ? ` — ${destino.complemento}` : ""}`)} · ${esc(juntar(destino.bairro, `${destino.cidade}/${destino.uf}`, formatCep(destino.cep)))}`
    : "Sem endereço de envio";
  const valores: Record<string, [string, string]> = {
    clinica: ["Clínica", esc(p.clinica || destino?.destinatario || "—")],
    dentista: ["Dentista", esc(p.dentista || "—")],
    paciente: ["Paciente", esc(p.paciente)],
    endereco: ["Endereço", endereco],
    telefone: ["Telefone", esc(destino?.telefone || "—")],
    pedido: ["Pedido", esc(codigoDoPedido(p))],
    prazo: ["Prazo", esc(data(p.prazo))],
  };
  const campos = cfg.camposExpedicao
    .filter((c) => c.ligado)
    .map((c) => celula(c.chave, valores[c.chave]![0], valores[c.chave]![1]))
    .join("");
  const logoHtml = cfg.logo && logo ? `<img class="logo" src="${esc(logo)}" alt="">` : "";
  return `<div class="parte exp${qrCatalogo ? " comqr" : ""}">${logoHtml}<div class="grade">${campos}</div>${qrCatalogo}</div>`;
}

/**
 * Etiqueta única, uma por serviço: produção em cima (vai com a peça na bancada) e expedição embaixo
 * (vai na caixa), separadas pela linha de corte. Tamanho, altura da expedição e campos vêm da
 * configuração do laboratório; expedição com altura 0 sai só a produção.
 */
export function paginaUnica(
  cfg: EtiquetaUnica,
  pedido: PedidoDaEtiqueta,
  servicos: ServicoDaEtiqueta[],
  destino: EntregaDoPedido | null,
  urlDoPedido: string,
  logo?: string | null,
  /** Link público do catálogo; nulo (catálogo desligado) = sem QR do catálogo, mesmo marcado. */
  urlDoCatalogo?: string | null,
): Pagina {
  const qr = cfg.qr ? qrDoPedido(urlDoPedido) : "";
  const comExpedicao = cfg.alturaDaExpedicao > 0;
  const qrCatalogo = cfg.qrCatalogo && urlDoCatalogo ? qrDoCatalogo(urlDoCatalogo) : "";
  const expedicao = comExpedicao ? parteDeExpedicao(cfg, pedido, destino, logo, qrCatalogo) : "";
  const corte = comExpedicao ? `<div class="corte">${TESOURA}<span class="linha"></span><span>RECORTE AQUI</span><span class="linha"></span></div>` : "";
  const corpo = servicos.map((s) => `<section class="u">${parteDeProducao(cfg, pedido, s, qr)}${corte}${expedicao}</section>`).join("");
  return { css: css(cfg), corpo };
}

/** Link que o QR abre: o pedido na lista de pedidos do laboratório. */
export const urlDoPedido = (id: string) => `${window.location.origin}/lab/pedidos?abrir=${encodeURIComponent(id)}`;
